import { Editor } from '@tiptap/react';
import {
  useCreateEditor,
  UseCreateEditorReturn,
} from 'components/RichTextEditor/hooks/useCreateEditor';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useAppSettings } from 'providers/contexts/SettingsContext';
import { createContext, memo, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditorSetupConfig,
  getAddressConfig,
  getAltTextConfig,
  getAuthorsConfig,
  getBylineConfig,
  getCanonicalUrlConfig,
  getContentConfig,
  getFlagsConfig,
  getHammerConfig,
  getHtmlConfig,
  getKeywordsConfig,
  getKickerConfig,
  getLatitudeConfig,
  getLinkConfig,
  getLongitudeConfig,
  getMunicipalityConfig,
  getPostalCodeConfig,
  getPrintHeadlineConfig,
  getPrintJumpHeadConfig,
  getPrintJumpKeywordConfig,
  getPrintNotesConfig,
  getPrintPlacementPriorityConfig,
  getPrintSlugConfig,
  getPrintSubheadlineConfig,
  getPriorityConfig,
  getSectionsConfig,
  getSeoDescriptionConfig,
  getSeoTitleConfig,
  getSiteTagsConfig,
  getSocialSummaryConfig,
  getSocialTitleConfig,
  getSourceConfig,
  getSubheadlineConfig,
  getSummaryConfig,
  getTaglineConfig,
  getTitleConfig,
  getUrlTitleConfig,
} from '../utility/getEditorConfigs';
import { useAssetConfigContext } from './AssetConfigContextProvider';
import { useNxtSyncStore, UseNxtSyncStoreRtn } from './SyncedStores/UseNxtSyncStore';

// -----------------------------------------------------------------
// Types
// -----------------------------------------------------------------

export type AssetEditorsContextValuesType = {
  // --- Editors from Setup Hook ---
  altTextEditor: Editor | null;
  contentEditor: Editor | null; // 'body copy / main content'
  authorsEditor: Editor | null;
  hammerEditor: Editor | null;
  kickerEditor: Editor | null;
  titleEditor: Editor | null;
  bylineEditor: Editor | null;
  subheadlineEditor: Editor | null;
  htmlEditor: Editor | null; // Html Assets properties.html
  linkEditor: Editor | null; // Link Assets properties.link
  sectionsEditor: Editor | null;
  summaryEditor: Editor | null;
  keywordsEditor: Editor | null;
  flagsEditor: Editor | null;
  siteTagsEditor: Editor | null; // site_tags
  priorityEditor: Editor | null; // properties.priority
  sourceEditor: Editor | null; // properties.source
  printHeadlineEditor: Editor | null;
  printJumpHeadEditor: Editor | null;
  printJumpKeywordEditor: Editor | null;
  printNotesEditor: Editor | null;
  printPlacementPriorityEditor: Editor | null;
  printSlugEditor: Editor | null;
  printSubheadlineEditor: Editor | null;
  addressEditor: Editor | null;
  municipalityEditor: Editor | null;
  postalCodeEditor: Editor | null;
  latitudeEditor: Editor | null;
  longitudeEditor: Editor | null;
  taglineEditor: Editor | null;
  seoTitleEditor: Editor | null;
  canonicalUrlEditor: Editor | null;
  seoDescriptionEditor: Editor | null;
  urlTitleEditor: Editor | null;
  socialTitleEditor: Editor | null;
  socialSummaryEditor: Editor | null;

  // --- Editor Hook Setups ---
  altTextEditorHook: UseCreateEditorReturn;
  contentEditorHook: UseCreateEditorReturn;
  authorsEditorHook: UseCreateEditorReturn;
  hammerEditorHook: UseCreateEditorReturn;
  kickerEditorHook: UseCreateEditorReturn;
  titleEditorHook: UseCreateEditorReturn;
  bylineEditorHook: UseCreateEditorReturn;
  subheadlineEditorHook: UseCreateEditorReturn;
  htmlEditorHook: UseCreateEditorReturn;
  linkEditorHook: UseCreateEditorReturn;
  sectionsEditorHook: UseCreateEditorReturn;
  summaryEditorHook: UseCreateEditorReturn;
  keywordsEditorHook: UseCreateEditorReturn;
  flagsEditorHook: UseCreateEditorReturn;
  siteTagsEditorHook: UseCreateEditorReturn;
  priorityEditorHook: UseCreateEditorReturn;
  sourceEditorHook: UseCreateEditorReturn;
  printHeadlineEditorHook: UseCreateEditorReturn;
  printJumpHeadEditorHook: UseCreateEditorReturn;
  printJumpKeywordEditorHook: UseCreateEditorReturn;
  printNotesEditorHook: UseCreateEditorReturn;
  printPlacementPriorityEditorHook: UseCreateEditorReturn;
  printSlugEditorHook: UseCreateEditorReturn;
  printSubheadlineEditorHook: UseCreateEditorReturn;
  addressEditorHook: UseCreateEditorReturn;
  municipalityEditorHook: UseCreateEditorReturn;
  postalCodeEditorHook: UseCreateEditorReturn;
  latitudeEditorHook: UseCreateEditorReturn;
  longitudeEditorHook: UseCreateEditorReturn;
  taglineEditorHook: UseCreateEditorReturn;
  seoTitleEditorHook: UseCreateEditorReturn;
  canonicalUrlEditorHook: UseCreateEditorReturn;
  seoDescriptionEditorHook: UseCreateEditorReturn;
  urlTitleEditorHook: UseCreateEditorReturn;
  socialTitleEditorHook: UseCreateEditorReturn;
  socialSummaryEditorHook: UseCreateEditorReturn;
  // --- Editor Configurations ---
  altTextEditorConfig: EditorSetupConfig | undefined;
  contentEditorConfig: EditorSetupConfig | undefined;
  authorsEditorConfig: EditorSetupConfig | undefined;
  hammerEditorConfig: EditorSetupConfig | undefined;
  kickerEditorConfig: EditorSetupConfig | undefined;
  titleEditorConfig: EditorSetupConfig | undefined;
  bylineEditorConfig: EditorSetupConfig | undefined;
  subheadlineEditorConfig: EditorSetupConfig | undefined;
  htmlEditorConfig: EditorSetupConfig | undefined;
  linkEditorConfig: EditorSetupConfig | undefined;
  sectionsEditorConfig: EditorSetupConfig | undefined;
  summaryEditorConfig: EditorSetupConfig | undefined;
  keywordsEditorConfig: EditorSetupConfig | undefined;
  flagsEditorConfig: EditorSetupConfig | undefined;
  siteTagsEditorConfig: EditorSetupConfig | undefined;
  priorityEditorConfig: EditorSetupConfig | undefined;
  sourceEditorConfig: EditorSetupConfig | undefined;
  printHeadlineEditorConfig: EditorSetupConfig | undefined;
  printJumpHeadEditorConfig: EditorSetupConfig | undefined;
  printJumpKeywordEditorConfig: EditorSetupConfig | undefined;
  printNotesEditorConfig: EditorSetupConfig | undefined;
  printPlacementPriorityEditorConfig: EditorSetupConfig | undefined;
  printSlugEditorConfig: EditorSetupConfig | undefined;
  printSubheadlineEditorConfig: EditorSetupConfig | undefined;
  addressEditorConfig: EditorSetupConfig | undefined;
  municipalityEditorConfig: EditorSetupConfig | undefined;
  postalCodeEditorConfig: EditorSetupConfig | undefined;
  latitudeEditorConfig: EditorSetupConfig | undefined;
  longitudeEditorConfig: EditorSetupConfig | undefined;
  taglineEditorConfig: EditorSetupConfig | undefined;
  seoTitleEditorConfig: EditorSetupConfig | undefined;
  canonicalUrlEditorConfig: EditorSetupConfig | undefined;
  seoDescriptionEditorConfig: EditorSetupConfig | undefined;
  urlTitleEditorConfig: EditorSetupConfig | undefined;
  socialTitleEditorConfig: EditorSetupConfig | undefined;
  socialSummaryEditorConfig: EditorSetupConfig | undefined;
  // --- YDoc Sync Store ---
  nxtSyncStore: UseNxtSyncStoreRtn;
  // --- Collection Insert Handler ---
  collectionInsertHandler: ((args: any) => void) | undefined;
  setCollectionInsertHandler: (handler: ((args: any) => void) | undefined) => void;
};

export type AssetEditorsContextProviderProps = {
  children?: React.ReactNode;
};

// --------------------------------------------------------------------
// AssetEditorsContextProvider
// Please do your best to keep this component as performant and stable
// as possible to minimize the number of re-renders it must perform
// which can have unintended side effects on the tiptap collab editors.
// --------------------------------------------------------------------

export const AssetEditorsContext = createContext<
  AssetEditorsContextValuesType | undefined
>(undefined);

export const useAssetEditorsContext = () => {
  const context = useContext(AssetEditorsContext);
  if (!context) {
    throw new Error(
      'useAssetEditorsContext must be used within an AssetEditorsContextProvider',
    );
  }
  return context;
};

export const AssetEditorsContextProvider = memo(
  ({ children }: AssetEditorsContextProviderProps) => {
    const { t } = useTranslation();
    const { appSettings } = useAppSettings();
    const { sessionConfig } = useCosmos();
    const {
      yDoc,
      recordCopy,
      collabProvider,
      syncedInitialYDocConfig,
      isReadOnlyMode,
      isImportMode,
      collabUser,
    } = useAssetConfigContext();

    // Collection insert handler state
    const [collectionInsertHandler, setCollectionInsertHandler] = useState<
      ((args: any) => void) | undefined
    >(undefined);

    // --- editor configurations ---
    const contentEditorConfig = useMemo(
      () =>
        getContentConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const authorsEditorConfig = useMemo(
      () =>
        getAuthorsConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const hammerEditorConfig = useMemo(
      () =>
        getHammerConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const kickerEditorConfig = useMemo(
      () =>
        getKickerConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const titleEditorConfig = useMemo(
      () =>
        getTitleConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const bylineEditorConfig = useMemo(
      () =>
        getBylineConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const subheadlineEditorConfig = useMemo(
      () =>
        getSubheadlineConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );

    const htmlEditorConfig = useMemo(
      () =>
        getHtmlConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );

    const linkEditorConfig = useMemo(
      () =>
        getLinkConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const sectionsEditorConfig = useMemo(
      () =>
        getSectionsConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const summaryEditorConfig = useMemo(
      () =>
        getSummaryConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const keywordsEditorConfig = useMemo(
      () =>
        getKeywordsConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const flagsEditorConfig = useMemo(
      () =>
        getFlagsConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const siteTagsEditorConfig = useMemo(
      () =>
        getSiteTagsConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const priorityEditorConfig = useMemo(
      () =>
        getPriorityConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const sourceEditorConfig = useMemo(
      () =>
        getSourceConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printHeadlineEditorConfig = useMemo(
      () =>
        getPrintHeadlineConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printJumpHeadEditorConfig = useMemo(
      () =>
        getPrintJumpHeadConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printJumpKeywordEditorConfig = useMemo(
      () =>
        getPrintJumpKeywordConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printNotesEditorConfig = useMemo(
      () =>
        getPrintNotesConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printPlacementPriorityEditorConfig = useMemo(
      () =>
        getPrintPlacementPriorityConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const printSlugEditorConfig = useMemo(
      () =>
        getPrintSlugConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );

    const printSubheadlineEditorConfig = useMemo(
      () =>
        getPrintSubheadlineConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const addressEditorConfig = useMemo(
      () =>
        getAddressConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const municipalityEditorConfig = useMemo(
      () =>
        getMunicipalityConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const postalCodeEditorConfig = useMemo(
      () =>
        getPostalCodeConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const latitudeEditorConfig = useMemo(
      () =>
        getLatitudeConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const longitudeEditorConfig = useMemo(
      () =>
        getLongitudeConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const taglineEditorConfig = useMemo(
      () =>
        getTaglineConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const seoTitleEditorConfig = useMemo(
      () =>
        getSeoTitleConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const canonicalUrlEditorConfig = useMemo(
      () =>
        getCanonicalUrlConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const seoDescriptionEditorConfig = useMemo(
      () =>
        getSeoDescriptionConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const urlTitleEditorConfig = useMemo(
      () =>
        getUrlTitleConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, isImportMode, t],
    );
    const socialTitleEditorConfig = useMemo(
      () =>
        getSocialTitleConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const socialSummaryEditorConfig = useMemo(
      () =>
        getSocialSummaryConfig({
          record: recordCopy,
          isReadOnlyMode: isReadOnlyMode,
          isImportMode: isImportMode,
        }),
      [recordCopy, isReadOnlyMode, isImportMode],
    );
    const altTextEditorConfig = useMemo(
      () =>
        getAltTextConfig({
          record: recordCopy,
          isReadOnlyMode,
          t,
        }),
      [recordCopy, isReadOnlyMode, t],
    );

    // Note on the useCreateEditor (useEditor) dependency array:
    // Although xEditorConfig is recalculated as its derived from record
    // ideally they should be in the editor deps array, however upon save
    // since the recordCopy changes, the configs are recalc which causes
    // the editor to rerender and reset the scroll position in the UI
    // which is really annoying on autosaves. If you have a static placeholder
    // that is fine but if you need a more dynamic placholder that is
    // derived from the live editor data content, consider dynamically updating
    // it via the editor onUpdate handler which wont cause a hard rerender.
    // https://tiptap.dev/docs/editor/api/events (see also useCalcEditorState)
    // attach the editor.on('update') + editor.on('create') event handlers
    // and then update the dynamic placeholder based on the event update.
    // eg: updateTiptapPlaceholder / editorUtilities.ts / SeoSettings.tsx

    // --- Setup editors via useCreateEditor hook ---
    const contentEditorHook = useCreateEditor(
      {
        editorConfig: contentEditorConfig,
        sessionConfig: sessionConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const contentEditor = useMemo(() => {
      return contentEditorHook?.editor;
    }, [contentEditorHook?.editor]);

    const titleEditorHook = useCreateEditor(
      {
        editorConfig: titleEditorConfig,
        sessionConfig: sessionConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const titleEditor = useMemo(() => {
      return titleEditorHook?.editor;
    }, [titleEditorHook?.editor]);

    const authorsEditorHook = useCreateEditor(
      {
        editorConfig: authorsEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const authorsEditor = useMemo(() => {
      return authorsEditorHook?.editor;
    }, [authorsEditorHook?.editor]);

    const hammerEditorHook = useCreateEditor(
      {
        editorConfig: hammerEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const hammerEditor = useMemo(() => {
      return hammerEditorHook?.editor;
    }, [hammerEditorHook?.editor]);

    const kickerEditorHook = useCreateEditor(
      {
        editorConfig: kickerEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const kickerEditor = useMemo(() => {
      return kickerEditorHook?.editor;
    }, [kickerEditorHook?.editor]);

    const bylineEditorHook = useCreateEditor(
      {
        editorConfig: bylineEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const bylineEditor = useMemo(() => {
      return bylineEditorHook?.editor;
    }, [bylineEditorHook?.editor]);

    const subheadlineEditorHook = useCreateEditor(
      {
        editorConfig: subheadlineEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const subheadlineEditor = useMemo(() => {
      return subheadlineEditorHook?.editor;
    }, [subheadlineEditorHook?.editor]);

    const htmlEditorHook = useCreateEditor(
      {
        editorConfig: htmlEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const htmlEditor = useMemo(() => {
      return htmlEditorHook?.editor;
    }, [htmlEditorHook?.editor]);

    const linkEditorHook = useCreateEditor(
      {
        editorConfig: linkEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const linkEditor = useMemo(() => {
      return linkEditorHook?.editor;
    }, [linkEditorHook?.editor]);

    const sectionsEditorHook = useCreateEditor(
      {
        editorConfig: sectionsEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const sectionsEditor = useMemo(() => {
      return sectionsEditorHook?.editor;
    }, [sectionsEditorHook?.editor]);

    const summaryEditorHook = useCreateEditor(
      {
        editorConfig: summaryEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const summaryEditor = useMemo(() => {
      return summaryEditorHook?.editor;
    }, [summaryEditorHook?.editor]);

    const keywordsEditorHook = useCreateEditor(
      {
        editorConfig: keywordsEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const keywordsEditor = useMemo(() => {
      return keywordsEditorHook?.editor;
    }, [keywordsEditorHook?.editor]);

    const flagsEditorHook = useCreateEditor(
      {
        editorConfig: flagsEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const flagsEditor = useMemo(() => {
      return flagsEditorHook?.editor;
    }, [flagsEditorHook?.editor]);

    const siteTagsEditorHook = useCreateEditor(
      {
        editorConfig: siteTagsEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const siteTagsEditor = useMemo(() => {
      return siteTagsEditorHook?.editor;
    }, [siteTagsEditorHook?.editor]);

    const priorityEditorHook = useCreateEditor(
      {
        editorConfig: priorityEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const priorityEditor = useMemo(() => {
      return priorityEditorHook?.editor;
    }, [priorityEditorHook?.editor]);

    const sourceEditorHook = useCreateEditor(
      {
        editorConfig: sourceEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const sourceEditor = useMemo(() => {
      return sourceEditorHook?.editor;
    }, [sourceEditorHook?.editor]);

    const printHeadlineEditorHook = useCreateEditor(
      {
        editorConfig: printHeadlineEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printHeadlineEditor = useMemo(() => {
      return printHeadlineEditorHook?.editor;
    }, [printHeadlineEditorHook?.editor]);

    const printJumpHeadEditorHook = useCreateEditor(
      {
        editorConfig: printJumpHeadEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printJumpHeadEditor = useMemo(() => {
      return printJumpHeadEditorHook?.editor;
    }, [printJumpHeadEditorHook?.editor]);

    const printJumpKeywordEditorHook = useCreateEditor(
      {
        editorConfig: printJumpKeywordEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printJumpKeywordEditor = useMemo(() => {
      return printJumpKeywordEditorHook?.editor;
    }, [printJumpKeywordEditorHook?.editor]);

    const printNotesEditorHook = useCreateEditor(
      {
        editorConfig: printNotesEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printNotesEditor = useMemo(() => {
      return printNotesEditorHook?.editor;
    }, [printNotesEditorHook?.editor]);

    const printPlacementPriorityEditorHook = useCreateEditor(
      {
        editorConfig: printPlacementPriorityEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printPlacementPriorityEditor = useMemo(() => {
      return printPlacementPriorityEditorHook?.editor;
    }, [printPlacementPriorityEditorHook?.editor]);

    const printSlugEditorHook = useCreateEditor(
      {
        editorConfig: printSlugEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printSlugEditor = useMemo(() => {
      return printSlugEditorHook?.editor;
    }, [printSlugEditorHook?.editor]);

    const printSubheadlineEditorHook = useCreateEditor(
      {
        editorConfig: printSubheadlineEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const printSubheadlineEditor = useMemo(() => {
      return printSubheadlineEditorHook?.editor;
    }, [printSubheadlineEditorHook?.editor]);

    const addressEditorHook = useCreateEditor(
      {
        editorConfig: addressEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const addressEditor = useMemo(() => {
      return addressEditorHook?.editor;
    }, [addressEditorHook?.editor]);

    const municipalityEditorHook = useCreateEditor(
      {
        editorConfig: municipalityEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const municipalityEditor = useMemo(() => {
      return municipalityEditorHook?.editor;
    }, [municipalityEditorHook?.editor]);

    const postalCodeEditorHook = useCreateEditor(
      {
        editorConfig: postalCodeEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const postalCodeEditor = useMemo(() => {
      return postalCodeEditorHook?.editor;
    }, [postalCodeEditorHook?.editor]);

    const latitudeEditorHook = useCreateEditor(
      {
        editorConfig: latitudeEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const latitudeEditor = useMemo(() => {
      return latitudeEditorHook?.editor;
    }, [latitudeEditorHook?.editor]);

    const longitudeEditorHook = useCreateEditor(
      {
        editorConfig: longitudeEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const longitudeEditor = useMemo(() => {
      return longitudeEditorHook?.editor;
    }, [longitudeEditorHook?.editor]);

    const taglineEditorHook = useCreateEditor(
      {
        editorConfig: taglineEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
        withLogging: true,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const taglineEditor = useMemo(() => {
      return taglineEditorHook?.editor;
    }, [taglineEditorHook?.editor]);

    const seoTitleEditorHook = useCreateEditor(
      {
        editorConfig: seoTitleEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const seoTitleEditor = useMemo(() => {
      return seoTitleEditorHook?.editor;
    }, [seoTitleEditorHook?.editor]);

    const canonicalUrlEditorHook = useCreateEditor(
      {
        editorConfig: canonicalUrlEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const canonicalUrlEditor = useMemo(() => {
      return canonicalUrlEditorHook?.editor;
    }, [canonicalUrlEditorHook?.editor]);

    const seoDescriptionEditorHook = useCreateEditor(
      {
        editorConfig: seoDescriptionEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const seoDescriptionEditor = useMemo(() => {
      return seoDescriptionEditorHook?.editor;
    }, [seoDescriptionEditorHook?.editor]);

    const urlTitleEditorHook = useCreateEditor(
      {
        editorConfig: urlTitleEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const urlTitleEditor = useMemo(() => {
      return urlTitleEditorHook?.editor;
    }, [urlTitleEditorHook?.editor]);

    const socialTitleEditorHook = useCreateEditor(
      {
        editorConfig: socialTitleEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const socialTitleEditor = useMemo(() => {
      return socialTitleEditorHook?.editor;
    }, [socialTitleEditorHook?.editor]);

    const socialSummaryEditorHook = useCreateEditor(
      {
        editorConfig: socialSummaryEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const socialSummaryEditor = useMemo(() => {
      return socialSummaryEditorHook?.editor;
    }, [socialSummaryEditorHook?.editor]);

    const altTextEditorHook = useCreateEditor(
      {
        editorConfig: altTextEditorConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        sessionConfig: sessionConfig,
        syncedInitialYDocConfig: syncedInitialYDocConfig,
        bloxDoc: recordCopy,
        appSettings: appSettings,
      },
      [collabProvider, collabUser, sessionConfig, syncedInitialYDocConfig],
    );

    const altTextEditor = useMemo(() => {
      return altTextEditorHook?.editor;
    }, [altTextEditorHook?.editor]);

    // --- ydoc sync store ---
    const nxtSyncStore = useNxtSyncStore({
      yDoc: yDoc,
      bloxDoc: recordCopy,
      collabProvider: collabProvider,
      syncedInitialYDocConfig: syncedInitialYDocConfig,
      isReadOnlyMode: isReadOnlyMode,
    });

    // Memoized context value
    const contextValue = useMemo(() => {
      return {
        // --- ydoc sync store ---
        nxtSyncStore,
        // --- Collection Insert Handler ---
        collectionInsertHandler,
        setCollectionInsertHandler,
        // --- Editors from Setup Hook ---
        contentEditor,
        authorsEditor,
        hammerEditor,
        kickerEditor,
        titleEditor,
        bylineEditor,
        subheadlineEditor,
        htmlEditor,
        linkEditor,
        sectionsEditor,
        summaryEditor,
        keywordsEditor,
        flagsEditor,
        siteTagsEditor,
        priorityEditor,
        sourceEditor,
        printHeadlineEditor,
        printJumpHeadEditor,
        printJumpKeywordEditor,
        printNotesEditor,
        printPlacementPriorityEditor,
        printSlugEditor,
        printSubheadlineEditor,
        addressEditor,
        municipalityEditor,
        postalCodeEditor,
        latitudeEditor,
        longitudeEditor,
        taglineEditor,
        seoTitleEditor,
        canonicalUrlEditor,
        seoDescriptionEditor,
        urlTitleEditor,
        socialTitleEditor,
        socialSummaryEditor,

        // --- Editor Hook Setups ---
        contentEditorHook,
        authorsEditorHook,
        hammerEditorHook,
        kickerEditorHook,
        titleEditorHook,
        bylineEditorHook,
        subheadlineEditorHook,
        htmlEditorHook,
        linkEditorHook,
        sectionsEditorHook,
        summaryEditorHook,
        keywordsEditorHook,
        flagsEditorHook,
        siteTagsEditorHook,
        priorityEditorHook,
        sourceEditorHook,
        printHeadlineEditorHook,
        printJumpHeadEditorHook,
        printJumpKeywordEditorHook,
        printNotesEditorHook,
        printPlacementPriorityEditorHook,
        printSlugEditorHook,
        printSubheadlineEditorHook,
        addressEditorHook,
        municipalityEditorHook,
        postalCodeEditorHook,
        latitudeEditorHook,
        longitudeEditorHook,
        taglineEditorHook,
        seoTitleEditorHook,
        canonicalUrlEditorHook,
        seoDescriptionEditorHook,
        urlTitleEditorHook,
        socialTitleEditorHook,
        socialSummaryEditorHook,

        // --- Editor Configurations ---
        contentEditorConfig,
        authorsEditorConfig,
        hammerEditorConfig,
        kickerEditorConfig,
        titleEditorConfig,
        bylineEditorConfig,
        subheadlineEditorConfig,
        htmlEditorConfig,
        linkEditorConfig,
        sectionsEditorConfig,
        summaryEditorConfig,
        keywordsEditorConfig,
        flagsEditorConfig,
        siteTagsEditorConfig,
        priorityEditorConfig,
        sourceEditorConfig,
        printHeadlineEditorConfig,
        printJumpHeadEditorConfig,
        printJumpKeywordEditorConfig,
        printNotesEditorConfig,
        printPlacementPriorityEditorConfig,
        printSlugEditorConfig,
        printSubheadlineEditorConfig,
        addressEditorConfig,
        municipalityEditorConfig,
        postalCodeEditorConfig,
        latitudeEditorConfig,
        longitudeEditorConfig,
        taglineEditorConfig,
        seoTitleEditorConfig,
        canonicalUrlEditorConfig,
        seoDescriptionEditorConfig,
        urlTitleEditorConfig,
        socialTitleEditorConfig,
        socialSummaryEditorConfig,
        altTextEditor,
        altTextEditorHook,
        altTextEditorConfig,
      };
    }, [
      // --- ydoc sync store ---
      nxtSyncStore,
      // --- Editors from Setup Hook ---
      contentEditor,
      authorsEditor,
      hammerEditor,
      kickerEditor,
      titleEditor,
      bylineEditor,
      subheadlineEditor,
      htmlEditor,
      linkEditor,
      sectionsEditor,
      summaryEditor,
      keywordsEditor,
      flagsEditor,
      siteTagsEditor,
      priorityEditor,
      sourceEditor,
      printHeadlineEditor,
      printJumpHeadEditor,
      printJumpKeywordEditor,
      printNotesEditor,
      printPlacementPriorityEditor,
      printSlugEditor,
      printSubheadlineEditor,
      addressEditor,
      municipalityEditor,
      postalCodeEditor,
      latitudeEditor,
      longitudeEditor,
      taglineEditor,
      seoTitleEditor,
      canonicalUrlEditor,
      seoDescriptionEditor,
      urlTitleEditor,
      socialTitleEditor,
      socialSummaryEditor,

      // --- Editor Hook Setups ---
      contentEditorHook,
      authorsEditorHook,
      hammerEditorHook,
      kickerEditorHook,
      titleEditorHook,
      bylineEditorHook,
      subheadlineEditorHook,
      htmlEditorHook,
      linkEditorHook,
      sectionsEditorHook,
      summaryEditorHook,
      keywordsEditorHook,
      flagsEditorHook,
      siteTagsEditorHook,
      priorityEditorHook,
      sourceEditorHook,
      printHeadlineEditorHook,
      printJumpHeadEditorHook,
      printJumpKeywordEditorHook,
      printNotesEditorHook,
      printPlacementPriorityEditorHook,
      printSlugEditorHook,
      printSubheadlineEditorHook,
      addressEditorHook,
      municipalityEditorHook,
      postalCodeEditorHook,
      latitudeEditorHook,
      longitudeEditorHook,
      taglineEditorHook,
      seoTitleEditorHook,
      canonicalUrlEditorHook,
      seoDescriptionEditorHook,
      urlTitleEditorHook,
      socialTitleEditorHook,
      socialSummaryEditorHook,

      // --- Editor Configurations ---
      contentEditorConfig,
      authorsEditorConfig,
      hammerEditorConfig,
      kickerEditorConfig,
      titleEditorConfig,
      bylineEditorConfig,
      subheadlineEditorConfig,
      htmlEditorConfig,
      linkEditorConfig,
      sectionsEditorConfig,
      summaryEditorConfig,
      keywordsEditorConfig,
      flagsEditorConfig,
      siteTagsEditorConfig,
      priorityEditorConfig,
      sourceEditorConfig,
      printHeadlineEditorConfig,
      printJumpHeadEditorConfig,
      printJumpKeywordEditorConfig,
      printNotesEditorConfig,
      printPlacementPriorityEditorConfig,
      printSlugEditorConfig,
      printSubheadlineEditorConfig,
      addressEditorConfig,
      municipalityEditorConfig,
      postalCodeEditorConfig,
      latitudeEditorConfig,
      longitudeEditorConfig,
      taglineEditorConfig,
      seoTitleEditorConfig,
      canonicalUrlEditorConfig,
      seoDescriptionEditorConfig,
      urlTitleEditorConfig,
      socialTitleEditorConfig,
      socialSummaryEditorConfig,
      altTextEditor,
      altTextEditorHook,
      altTextEditorConfig,
      // --- Collection Insert Handler ---
      collectionInsertHandler,
      setCollectionInsertHandler,
    ]);

    return (
      <AssetEditorsContext.Provider value={contextValue}>
        {children}
      </AssetEditorsContext.Provider>
    );
  },
);

AssetEditorsContextProvider.displayName = 'AssetEditorsContextProvider';
export default AssetEditorsContextProvider;
